import { useState } from 'react'

import { api, downloadBlob } from '../api/client'
import { Card, EmptyState, ErrorBanner, Spinner } from '../components/common'
import { DataTable } from '../components/DataTable'
import type { Column } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import type { VInventory as DatiInventario } from '../api/types'

/**
 * Report d'inventario dell'ambiente virtuale, **tipo RVTools**: si sceglie un
 * ambiente (un cluster, o tutto il parco), si vedono i fogli a video — VM, host,
 * cluster, datastore, reti, con le colonne filtrabili — e si esporta l'Excel
 * multi-foglio. I dati vengono dalla CMDB (connettori di virtualizzazione).
 */
export default function VInventory() {
  return (
    <InventarioAFogli
      titolo={t('Inventario virtuale')}
      sottotitolo={t('censisce VM, host, cluster, datastore e reti rilevati dai connettori di virtualizzazione, consultabili a video o esportabili in Excel, con un livello di dettaglio analogo a RVTools.')}
      tutto={t('Tutto l’ambiente virtuale')}
      file="censo-vinventory"
      vuotoTitolo={t('Nessun oggetto virtuale')}
      vuotoHint={t('Configura un connettore di virtualizzazione (VMware, Nutanix, Proxmox…) e lancia una raccolta.')}
      ambienti={() => api.vinventoryEnvironments()}
      dati={(env) => api.vinventory(env)}
      esportazione={(fmt, env) => api.vinventoryExportPath(fmt, env)}
    />
  )
}

/**
 * La pagina a fogli condivisa da Inventario virtuale e Inventario cloud: si
 * sceglie un ambiente, si guardano i fogli, si esporta Excel o PDF.
 */
export function InventarioAFogli({
  titolo,
  sottotitolo,
  tutto,
  file,
  vuotoTitolo,
  vuotoHint,
  ambienti: caricaAmbienti,
  dati: caricaDati,
  esportazione,
  iniziale = '',
}: {
  titolo: string
  sottotitolo: string
  tutto: string
  file: string
  vuotoTitolo: string
  vuotoHint: string
  ambienti: () => Promise<{ environments: { id: string; name: string }[] }>
  dati: (env?: string) => Promise<DatiInventario>
  esportazione: (fmt: 'xlsx' | 'pdf', env?: string) => string
  iniziale?: string
}) {
  const ambienti = useAsync(caricaAmbienti, [])
  const [env, setEnv] = useState(iniziale)
  const dati = useAsync(() => caricaDati(env || undefined), [env])
  const [foglio, setFoglio] = useState<string | null>(null)
  const [scaricando, setScaricando] = useState(false)
  const [erroreDl, setErroreDl] = useState<unknown>(null)

  const sheets = dati.data?.sheets ?? []
  const attivo = sheets.find((s) => s.key === foglio) ?? sheets[0]

  async function esporta(fmt: 'xlsx' | 'pdf') {
    setScaricando(true)
    setErroreDl(null)
    try {
      const blob = await downloadBlob(esportazione(fmt, env || undefined))
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${file}.${fmt}`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      setErroreDl(e)
    } finally {
      setScaricando(false)
    }
  }

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{titolo}</h1>
          <p className="page__subtitle">{sottotitolo}</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => esporta('xlsx')} disabled={scaricando || !sheets.length}>
            {scaricando ? t('Esporto…') : t('Esporta Excel')}
          </button>
          <button className="secondary" onClick={() => esporta('pdf')} disabled={scaricando || !sheets.length}>
            {t('Esporta PDF')}
          </button>
        </div>
      </header>

      <ErrorBanner error={ambienti.error ?? dati.error ?? erroreDl} />

      <Card title={t('Ambiente')}>
        <label className="field" style={{ maxWidth: 360, margin: 0 }}>
          <span className="field__label">{t('Ambiente')}</span>
          <select value={env} onChange={(e) => setEnv(e.target.value)}>
            <option value="">{tutto}</option>
            {(ambienti.data?.environments ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </label>
      </Card>

      {dati.loading && !dati.data ? (
        <Spinner />
      ) : !sheets.length ? null : sheets.every((s) => s.rows.length === 0) ? (
        <EmptyState title={vuotoTitolo} hint={vuotoHint} />
      ) : (
        <Card
          title={t('Inventario')}
          actions={
            <div className="tabs" style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {sheets.map((s) => (
                <button
                  key={s.key}
                  className={s.key === attivo?.key ? '' : 'secondary'}
                  onClick={() => setFoglio(s.key)}
                >
                  {t(s.title)} ({s.rows.length})
                </button>
              ))}
            </div>
          }
        >
          {attivo && attivo.rows.length === 0 ? (
            <p className="muted">{t('Nessuna riga in questo foglio.')}</p>
          ) : attivo ? (
            <DataTable
              rows={attivo.rows}
              rowKey={(r, i) => `${r[0]}-${i}`}
              columns={attivo.headers.map(
                (h, i): Column<string[]> => ({
                  key: String(i),
                  header: t(h),
                  value: (r) => r[i] ?? '',
                }),
              )}
            />
          ) : null}
        </Card>
      )}
    </>
  )
}
